import { useState, useEffect, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Building2, CheckCircle, XCircle, Clock, MapPin, Phone, Mail, Landmark, Trash2 } from "lucide-react";
import api, { formatDateTime, handleApiError } from "@/lib/api";
import { toast } from "sonner";
import { usePermission } from "@/hooks/usePermission";
import { useAuth } from "@/context/AuthContext";
import MfaBlockedBanner from "@/components/MfaBlockedBanner";

export default function AdminApprovals() {
  const { canEdit: canEditPerm } = usePermission("aprovacoes");
  const { user } = useAuth();
  // 2FA vencido: o backend recusa aprovar/recusar/apagar com 403 (FS-056).
  const mfaBlocked = !!user?.mfa_blocked;
  const canEdit = canEditPerm && !mfaBlocked;
  const [companies, setCompanies] = useState([]);
  const [credores, setCredores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [approveDialog, setApproveDialog] = useState(null);
  const [selectedCredor, setSelectedCredor] = useState("");
  const [rejectDialog, setRejectDialog] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [approveError, setApproveError] = useState("");

  const fetchCompanies = useCallback(async () => {
    try {
      const [compRes, credRes] = await Promise.all([
        api.get("/admin/pending-companies"),
        api.get("/admin/credores"),
      ]);
      setCompanies(compRes.data);
      setCredores(credRes.data);
    } catch { toast.error("Erro ao carregar empresas"); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchCompanies(); }, [fetchCompanies]);

  const handleApprove = async () => {
    if (!selectedCredor) { toast.error("Selecione um credor para aprovar a empresa"); return; }
    setProcessing(true);
    setApproveError("");
    try {
      const res = await api.post(`/admin/approve-company/${approveDialog}?credor_id=${selectedCredor}`);
      const company = companies.find(c => c.id === approveDialog);
      if (res.data?.payer_v2?.status === "pendente") {
        toast.warning("Empresa aprovada. Pagador pendente na Zemo — use Reprocessar.");
      } else {
        toast.success(`${company?.name || "Empresa"} aprovada e cadastrada como pagadora`);
      }
      setApproveDialog(null);
      setSelectedCredor("");
      fetchCompanies();
    } catch (err) {
      const msg = handleApiError(err, "Erro ao aprovar");
      setApproveError(msg);
      toast.error(msg);
    }
    setProcessing(false);
  };

  const handleReject = async () => {
    setProcessing(true);
    try {
      await api.post(`/admin/reject-company/${rejectDialog}`);
      const company = companies.find(c => c.id === rejectDialog);
      toast.success(`${company?.name || "Empresa"} recusada`);
      setRejectDialog(null);
      fetchCompanies();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao recusar");
    }
    setProcessing(false);
  };

  const handleDelete = async (companyId) => {
    try {
      await api.delete(`/admin/company/${companyId}`);
      const company = companies.find(c => c.id === companyId);
      toast.success(`${company?.name || "Empresa"} removida`);
      fetchCompanies();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao remover");
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="loading-spinner w-8 h-8" /></div>;

  return (
    <div className="p-4 pt-16 lg:pt-4 lg:p-8 space-y-6" data-testid="admin-approvals">
      <div>
        <h1 className="text-xl lg:text-2xl font-bold tracking-tight flex items-center gap-2">
          <Clock className="w-5 h-5 text-yellow-400" /> Aprovação de Empresas
        </h1>
        <p className="text-sm text-muted-foreground mt-1">{companies.length} empresa{companies.length !== 1 ? "s" : ""} pendente{companies.length !== 1 ? "s" : ""}</p>
      </div>

      {mfaBlocked && <MfaBlockedBanner action="aprovar empresas" />}

      {companies.length === 0 ? (
        <Card className="border-white/5" data-testid="approvals-empty-state">
          <CardContent className="p-8 text-center text-muted-foreground">
            Nenhuma empresa aguardando aprovação
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {companies.map((c) => {
            const isRejected = c.status === "rejected";
            return (
            <Card key={c.id} className={`border-white/5 ${isRejected ? "opacity-40" : ""}`} data-testid={`approval-card-${c.id}`}>
              <CardContent className="p-5">
                <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                  <div className="space-y-3 flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-3">
                      <div className="p-2 rounded-lg bg-blue-500/10 shrink-0">
                        <Building2 className="w-5 h-5 text-blue-400" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-semibold text-base lg:text-lg truncate">{c.name}</p>
                        <p className="text-sm text-muted-foreground">CNPJ: {c.cnpj}</p>
                      </div>
                      <span className={`px-2 py-0.5 rounded text-xs font-semibold ${c.status === "pending" ? "bg-yellow-500/15 text-yellow-400" : "bg-red-500/15 text-red-400"}`}>
                        {c.status === "pending" ? "Pendente" : "Recusada"}
                      </span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-muted-foreground">
                      <div className="flex items-center gap-2 truncate"><Mail className="w-3.5 h-3.5 shrink-0" /> <span className="truncate">{c.owner_email}</span></div>
                      <div className="flex items-center gap-2"><Phone className="w-3.5 h-3.5 shrink-0" /> {c.phone || "-"}</div>
                      {c.address && (
                        <div className="flex items-center gap-2 col-span-1 sm:col-span-2">
                          <MapPin className="w-3.5 h-3.5 shrink-0" />
                          <span className="truncate">{c.address.street}, {c.address.number} - {c.address.neighborhood}, {c.address.city}/{c.address.uf}</span>
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">Responsável: {c.owner_name} | Cadastro: {formatDateTime(c.created_at)}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {!isRejected && (
                      <>
                        <Button data-testid={`approve-btn-${c.id}`} onClick={() => { setApproveError(""); setApproveDialog(c.id); }} disabled={!canEdit}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold">
                          <CheckCircle className="w-4 h-4 mr-2" /> Aprovar
                        </Button>
                        <Button data-testid={`reject-btn-${c.id}`} onClick={() => setRejectDialog(c.id)} disabled={!canEdit}
                          variant="outline" className="border-red-500/30 text-red-400 hover:bg-red-500/10">
                          <XCircle className="w-4 h-4 mr-2" /> Recusar
                        </Button>
                      </>
                    )}
                    {isRejected && (
                      <Button data-testid={`delete-btn-${c.id}`} onClick={() => handleDelete(c.id)} disabled={!canEdit}
                        variant="outline" className="border-red-500/30 text-red-400 hover:bg-red-500/10">
                        <Trash2 className="w-4 h-4 mr-2" /> Apagar
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
            );
          })}
        </div>
      )}

      <Dialog open={!!approveDialog} onOpenChange={() => { setApproveDialog(null); setSelectedCredor(""); }}>
        <DialogContent className="sm:max-w-md border-white/10" data-testid="approve-dialog">
          <DialogHeader>
            <DialogTitle>Aprovar Empresa</DialogTitle>
            <DialogDescription>Selecione o credor e confirme a aprovação. A empresa será cadastrada como pagadora na Zemo.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Landmark className="w-4 h-4 text-blue-400" /> Credor <span className="text-red-400">*</span>
              </Label>
              {credores.length === 0 ? (
                <p className="text-sm text-yellow-400 p-3 bg-yellow-500/5 border border-yellow-500/10 rounded-lg">
                  Nenhum credor cadastrado. Cadastre um credor primeiro na aba Credores.
                </p>
              ) : (
                <Select value={selectedCredor} onValueChange={setSelectedCredor}>
                  <SelectTrigger data-testid="approve-credor-select" className="bg-black/20 border-white/10">
                    <SelectValue placeholder="Selecione o credor..." />
                  </SelectTrigger>
                  <SelectContent>
                    {credores.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.razao_social} ({c.cnpj})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>
          {approveError && (
            <p role="alert" data-testid="approve-error"
              className="text-sm text-red-400 p-3 bg-red-500/5 border border-red-500/20 rounded-lg">
              {approveError}
            </p>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setApproveDialog(null); setSelectedCredor(""); }} className="border-white/10">Cancelar</Button>
            <Button data-testid="confirm-approve-btn" onClick={handleApprove} disabled={processing || !selectedCredor || mfaBlocked}
              className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold">
              {processing ? <div className="loading-spinner" /> : "Confirmar Aprovação"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!rejectDialog} onOpenChange={() => setRejectDialog(null)}>
        <DialogContent className="sm:max-w-sm border-white/10" data-testid="reject-dialog">
          <DialogHeader>
            <DialogTitle>Recusar Empresa</DialogTitle>
            <DialogDescription>A empresa não poderá acessar a plataforma.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialog(null)} className="border-white/10">Cancelar</Button>
            <Button data-testid="confirm-reject-btn" onClick={handleReject} disabled={processing || mfaBlocked}
              className="bg-red-600 hover:bg-red-700 text-white font-semibold">
              {processing ? <div className="loading-spinner" /> : "Confirmar Recusa"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
